import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../components/ui/Button.tsx';
import GradientText from '../components/ui/GradientText.tsx';
import SEO from '../components/SEO.tsx';
import { useAuth } from '../contexts/AuthContext.tsx';
import { Mail, Rocket } from 'lucide-react';
import StripePopup from '../components/ui/StripePopup.tsx';
import { useStripePopup } from '../hooks/useStripePopup.ts';
import DemoVideoPopup from '../components/ui/DemoVideoPopup.tsx';

interface PlansProps {
  onNavigate: (page: string) => void;
}

interface License {
  _id: string;
  plan: {
    name: 'UNACTIVE' | 'FREE' | 'PRO' | 'ENTERPRISE';
    displayName: string;
  };
  isActive: boolean;
  issuedAt: string;
  expiresAt?: string;
  paymentStatus: 'incomplete' | 'incomplete_expired' | 'trialing' | 'active' | 'past_due' | 'canceled' | 'unpaid';
  createdByStripe: boolean;
  owner: {
    _id: string;
    firstname: string;
    lastname: string;
    email: string;
    phone: string;
  };
  __v: number;
}

const Plans: React.FC<PlansProps> = ({ onNavigate }) => {
  const { t } = useTranslation();
  const { isAuthenticated, createCheckoutSession, createTrialSession, getUserLicense } = useAuth();
  const [loading, setLoading] = useState<string | null>(null);
  const { popup, showPopup, hidePopup } = useStripePopup();
  const [userLicense, setUserLicense] = useState<License | null>(null);
  const [licenseLoading, setLicenseLoading] = useState(false);
  const features = [
    {
      feature: t('plans.features.addonsDeBase'),
      core_open_source: true,
      pro: true,
      premium: true
    },
    {
      feature: t('plans.features.sauvegardeBdd'),
      core_open_source: true,
      pro: true,
      premium: true
    },
    {
      feature: t('plans.features.alertAddons'),
      core_open_source: true,
      pro: true,
      premium: true
    },
    {
      feature: t('plans.features.webInterface'),
      core_open_source: false,
      pro: true,
      premium: true
    },
    {
      feature: t('plans.features.constructeurRegles'),
      core_open_source: false,
      pro: true,
      premium: true
    },
    {
      feature: t('plans.features.marcheRegles'),
      core_open_source: false,
      pro: true,
      premium: true
    },
    {
      feature: t('plans.features.historique'),
      core_open_source: false,
      pro: false,
      premium: true
    },
    {
      feature: t('plans.features.ia'),
      core_open_source: false,
      pro: false,
      premium: true
    },
    {
      feature: t('plans.features.supportPrioritaire'),
      core_open_source: false,
      pro: false,
      premium: true
    }
  ];

  useEffect(() => {
    const fetchUserLicense = async () => {
      if (isAuthenticated) {
        setLicenseLoading(true);
        try {
          const license = await getUserLicense();
          setUserLicense(license);
        } catch (error) {
          console.error('Error fetching license');
        } finally {
          setLicenseLoading(false);
        }
      }
    };

    fetchUserLicense();
  }, [isAuthenticated, getUserLicense]);

  const canUpgradeTo = (planType: 'pro' | 'enterprise'): boolean => {
    if (!isAuthenticated || !userLicense) return false;
    
    const currentPlan = userLicense.plan.name;
    
    if (planType === 'pro') {
      return currentPlan === 'FREE' || currentPlan === 'UNACTIVE';
    }
    
    if (planType === 'enterprise') {
      return currentPlan === 'FREE' || currentPlan === 'PRO' || currentPlan === 'UNACTIVE';
    }
    
    return false;
  };

  const handleUpgrade = async (plan: 'pro' | 'enterprise') => {
    if (!isAuthenticated) {
      onNavigate('auth');
      return;
    }

    if (!canUpgradeTo(plan)) {
      return;
    }

    try {
      setLoading(plan);
      const checkoutUrl = await createCheckoutSession(plan);
      window.location.href = checkoutUrl;
    } catch (error) {
      showPopup(error instanceof Error ? error.message : 'Payment failed', 'error');
    } finally {
      setLoading(null);
    }
  };

  const handleTrial = async (plan: 'pro') => {
    if (!isAuthenticated) {
      onNavigate('auth');
      return;
    }

    try {
      setLoading(`${plan}-trial`);
      const trialUrl = await createTrialSession(plan);
      window.location.href = trialUrl;
    } catch (error) {
      showPopup(error instanceof Error ? error.message : 'Trial failed', 'error');
    } finally {
      setLoading(null);
    }
  };

  const getButtonText = (planType?: 'pro' | 'enterprise') => {
    if (!planType) return t('plans.planTypes.coreOpenSource.cta');
    
    if (!isAuthenticated) {
      return 'Sign In to Upgrade';
    }
    
    if (licenseLoading) {
      return 'Loading...';
    }
    
    if (!canUpgradeTo(planType)) {
      return userLicense?.plan.name === planType.toUpperCase() ? 'Current Plan' : 'Not Available';
    }
    
    return planType === 'pro' ? t('plans.planTypes.pro.cta') : t('plans.planTypes.premium.cta');
  };

  const isButtonDisabled = (planType?: 'pro' | 'enterprise') => {
    if (!planType) return false;
    if (!isAuthenticated) return false;
    if (licenseLoading) return true;
    return !canUpgradeTo(planType) || loading === planType;
  };

  const plans = [
    {
      name: t('plans.planTypes.coreOpenSource.name'),
      price: "Free",
      description: t('plans.planTypes.coreOpenSource.description'),
      cta: getButtonText(),
      ctaAction: () => window.open('https://github.com/4urcloud/kexa', '_blank'),
      popular: false,
      features: features.filter(f => f.core_open_source).map(f => f.feature),
      gradient: "from-gray-600 to-gray-800",
      buttonStyle: "gray",
      disabled: isButtonDisabled()
    },
    {
      name: t('plans.planTypes.pro.name'),
      price: "€299/month",
      description: t('plans.planTypes.pro.description'),
      cta: getButtonText('pro'),
      ctaAction: () => handleUpgrade('pro'),
      popular: true,
      features: features.filter(f => f.pro).map(f => f.feature),
      gradient: "from-primary to-secondary",
      buttonStyle: "primary",
      planType: "pro" as const,
      disabled: isButtonDisabled('pro')
    },
    {
      name: t('plans.planTypes.premium.name'),
      price: "€599/month",
      description: t('plans.planTypes.premium.description'),
      cta: getButtonText('enterprise'),
      ctaAction: () => handleUpgrade('enterprise'),
      popular: false,
      features: features.filter(f => f.premium).map(f => f.feature),
      gradient: "from-secondary to-accent",
      buttonStyle: "gradient",
      planType: "enterprise" as const,
      disabled: isButtonDisabled('enterprise')
    }
  ];

  const CheckIcon = () => (
    <svg className="w-5 h-5 text-primary flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
    </svg>
  );

  const XIcon = () => (
    <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
    </svg>
  );

  return (
    <div className="min-h-screen pt-16">
      <DemoVideoPopup />
      <SEO
        title="Kexa Premium Pricing Plans - Cloud Security, finops and compliance"
        description="Choose the perfect Kexa Premium plan for your cloud security needs. From open source to enterprise-grade premium features with AI-powered monitoring and priority support."
        keywords="kexa pricing, cloud security pricing, compliance monitoring plans, enterprise security, premium cloud security, AWS monitoring pricing"
      />
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16 animate-slide-up">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            {t('plans.title.prefix')}
            <GradientText variant="primary">{t('plans.title.highlight')}</GradientText>
            {t('plans.title.suffix')}
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('plans.subtitle')}
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {plans.map((plan, index) => (
            <div
              key={index}
              className="relative bg-white rounded-2xl shadow-modern transition-all duration-300 hover:shadow-xl hover:-translate-y-1 flex flex-col h-full"
            >
              <div className={`bg-gradient-to-r ${plan.gradient} p-6 rounded-t-2xl`}>
                <h3 className="text-2xl font-bold text-white mb-2">
                  {plan.name}
                </h3>
                <div className="mb-2">
                  <span className="text-4xl font-bold text-white">
                    {plan.price}
                  </span>
                </div>
                <p className="text-white/90 text-sm">
                  {plan.description}
                </p>
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <div className="flex-1 mb-8">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">{t('plans.comparison.whatsIncluded')}</h4>
                  <div className="space-y-3">
                    {plan.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center space-x-3">
                        <CheckIcon />
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex flex-col gap-3">
                  {plan.buttonStyle === "gray" ? (
                    <button
                      onClick={plan.ctaAction}
                      disabled={plan.disabled}
                      className={`w-full h-12 px-8 bg-gradient-to-r from-gray-600 to-gray-800 text-white font-medium rounded-full transition-all duration-300 ease-in-out hover:from-gray-700 hover:to-gray-900 hover:shadow-lg transform hover:scale-105 active:scale-95 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-2 ${plan.disabled ? 'opacity-50 cursor-not-allowed hover:transform-none hover:scale-100' : ''}`}
                    >
                      {plan.cta}
                    </button>
                  ) : (
                    <>
                      {plan.planType === 'pro' && isAuthenticated && userLicense?.plan.name === 'FREE' && (
                        <Button
                          onClick={() => handleTrial('pro')}
                          variant="outline"
                          size="lg"
                          className="w-full"
                          disabled={loading === 'pro-trial' || licenseLoading}
                        >
                          {loading === 'pro-trial' ? t('plans.buttons.startingTrial') : t('plans.buttons.trySevenDaysFree')}
                        </Button>
                      )}
                      <Button
                        onClick={plan.ctaAction}
                        variant={plan.buttonStyle}
                        size="lg"
                        className="w-full"
                        disabled={plan.disabled}
                      >
                        {loading === plan.planType ? 'Processing...' : plan.cta}
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="bg-white rounded-2xl shadow-modern overflow-hidden">
          <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-6 border-b border-gray-200">
            <h2 className="text-2xl font-bold">
              <GradientText variant="primary">{t('plans.comparison.title.highlight')}</GradientText>
            </h2>
            <p className="text-gray-600 mt-2">{t('plans.comparison.subtitle')}</p>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 uppercase tracking-wider">
                    {t('plans.comparison.tableHeaders.features')}
                  </th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900 uppercase tracking-wider">
                    {t('plans.comparison.tableHeaders.coreOpenSource')}
                  </th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900 uppercase tracking-wider">
                    {t('plans.comparison.tableHeaders.pro')}
                  </th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900 uppercase tracking-wider">
                    {t('plans.comparison.tableHeaders.premium')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {features.map((feature, index) => (
                  <tr key={index} className={`transition-colors duration-200 hover:bg-gray-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      {feature.feature}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex justify-center">
                        {feature.core_open_source ? <CheckIcon /> : <XIcon />}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex justify-center">
                        {feature.pro ? <CheckIcon /> : <XIcon />}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex justify-center">
                        {feature.premium ? <CheckIcon /> : <XIcon />}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        </div>
      </section>
        <div className="mt-16 py-20 bg-gradient-hero relative overflow-hidden">
          <div className="absolute inset-0 bg-black/10"></div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-8 animate-slide-up">
              {t('common.buttons.readyToGetStarted')}
            </h2>
            <div className="w-16 h-px bg-white/40 mx-auto mb-8 animate-fade-in"></div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-fade-in">
            <Button
              onClick={() => onNavigate('deploy')}
              variant="cta"
              size="lg"
              leftIcon={<Rocket size={20} />}
            >
              {t('common.buttons.startDeploying')}
            </Button>
            <Button
              onClick={() => onNavigate('contact')}
              variant="cta-glass"
              size="lg"
              rightIcon={<Mail size={20} />}
            >
              {t('common.buttons.contactUs')}
            </Button>
            <Button
              onClick={() => onNavigate('roadmap')}
              variant="cta-glass"
              size="lg"
              rightIcon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                  </svg>}
            >
              {t('common.buttons.viewRoadmap')}
            </Button>
            </div>
          </div>
        </div>
      
      <StripePopup
        isOpen={popup.isOpen}
        onClose={hidePopup}
        message={popup.message}
        type={popup.type}
      />
    </div>
  );
};

export default Plans;